import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Calculator as CalculatorIcon,
  Clock3,
  FileText,
  Moon,
  Plane,
  Route,
  ShieldCheck,
  Sun,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import { PROPERTIES } from "@/data/dholera";
import { PARCEL_EXTRAS } from "@/data/parcelExtras";
import { inr, sqm } from "@/lib/format";
import { Button } from "@/components/ui/button";
import BlueprintBackground from "@/components/BlueprintBackground";
import Calculator from "@/components/Calculator";
import LeadModal from "@/components/LeadModal";

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

const MONO = "'Space Mono', monospace";

function SchemeMap({ index, zone, sector, plotCode }) {
  const ox = 44;
  const oy = 40;
  const cw = 116;
  const ch = 68;
  const road = 22;
  const plots = [];
  for (let r = 0; r < 2; r++)
    for (let c = 0; c < 3; c++)
      plots.push({
        x: ox + c * (cw + road),
        y: oy + r * (ch + road),
        code: `P-0${r * 3 + c + 1}`,
      });
  const hi = plots[index % plots.length];

  return (
    <svg
      viewBox="0 0 520 260"
      className="w-full text-muted-foreground"
      role="img"
      aria-label="TP scheme plot map"
      data-testid="scheme-map"
    >
      <rect x="10" y="10" width="500" height="240" fill="none" stroke="currentColor" strokeOpacity="0.3" />
      <rect x={ox} y={oy + ch} width={3 * cw + 2 * road} height={road} fill="currentColor" fillOpacity="0.08" />
      <rect x={ox + cw} y={oy} width={road} height={2 * ch + road} fill="currentColor" fillOpacity="0.08" />
      <rect x={ox + 2 * cw + road} y={oy} width={road} height={2 * ch + road} fill="currentColor" fillOpacity="0.08" />
      <text x={ox + 4} y={oy + ch + 15} fontSize="8" fontFamily={MONO} fill="currentColor" letterSpacing="2">
        30 M ROW
      </text>
      {plots.map((pl) => {
        const active = pl === hi;
        return (
          <g key={pl.code}>
            <rect
              x={pl.x}
              y={pl.y}
              width={cw}
              height={ch}
              fill={active ? "hsl(var(--accent) / 0.12)" : "transparent"}
              stroke={active ? "hsl(var(--accent))" : "currentColor"}
              strokeOpacity={active ? 1 : 0.4}
              strokeWidth={active ? 2 : 1}
            />
            <text
              x={pl.x + 8}
              y={pl.y + 18}
              fontSize="9"
              fontFamily={MONO}
              fill={active ? "hsl(var(--accent))" : "currentColor"}
              fontWeight={active ? 700 : 400}
            >
              {pl.code}
            </text>
          </g>
        );
      })}
      <text x={hi.x} y={hi.y - 8} fontSize="8" fontFamily={MONO} fill="hsl(var(--accent))" letterSpacing="2">
        LISTED PARCEL — {plotCode}
      </text>
      <g transform="translate(484 30)">
        <line x1="0" y1="14" x2="0" y2="0" stroke="currentColor" />
        <path d="M -4 5 L 0 -3 L 4 5" fill="none" stroke="currentColor" />
        <text x="-4" y="28" fontSize="9" fontFamily={MONO} fill="currentColor">
          N
        </text>
      </g>
      <text x="16" y="28" fontSize="9" fontFamily={MONO} fill="currentColor" letterSpacing="2">
        {zone} · SECTOR {sector} — SCHEME MAP
      </text>
      <g transform="translate(44 232)">
        <line x1="0" y1="0" x2="60" y2="0" stroke="currentColor" strokeWidth="2" />
        <line x1="0" y1="-3" x2="0" y2="3" stroke="currentColor" />
        <line x1="60" y1="-3" x2="60" y2="3" stroke="currentColor" />
        <text x="68" y="4" fontSize="8" fontFamily={MONO} fill="currentColor">
          100 M
        </text>
      </g>
    </svg>
  );
}

export default function ParcelDetail({ theme, onToggleTheme }) {
  const { id } = useParams();
  const parcel = PROPERTIES.find((p) => p.id === id);
  const extras = PARCEL_EXTRAS[id];
  const [activeImg, setActiveImg] = useState(0);
  const [calcOpen, setCalcOpen] = useState(false);
  const [lead, setLead] = useState(null);

  const calcSeed = useMemo(
    () =>
      parcel
        ? {
            area: parcel.plotSqYd,
            pps: parcel.pricePerSqYd,
            name: parcel.name,
          }
        : null,
    [parcel]
  );

  if (!parcel || !extras) {
    return (
      <div className="blueprint-bg flex min-h-screen items-center justify-center bg-background px-5">
        <div className="w-full max-w-sm border border-border bg-card p-8 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">404 — Off the map</p>
          <h1 className="mt-3 font-display text-2xl font-medium uppercase tracking-tight">
            Parcel not in index
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
            data-testid="parcel-notfound-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to marketplace
          </Link>
        </div>
      </div>
    );
  }

  const total = parcel.plotSqYd * parcel.pricePerSqYd;
  const registered = parcel.reraStatus === "REGISTERED";
  const gallery = [parcel.image, ...extras.gallery];
  const docs = [
    { name: "RERA Registration Certificate", ref: parcel.reraId, meta: "PDF · 1.2 MB" },
    { name: "Title Clearance & 7/12 Extract", ref: "Revenue Dept.", meta: "PDF · 840 KB" },
    { name: "TP Scheme Plot Map", ref: "Town Planning", meta: "CAD + PDF · 3.1 MB" },
    { name: "NA Order & Zoning Certificate", ref: "DSIRDA", meta: "PDF · 620 KB" },
  ];

  const requestDoc = (name) =>
    toast.info(`${name} — shared by your Relationship Manager after KYC verification.`);

  const metrics = [
    { label: "TP Scheme", value: parcel.zone },
    {
      label: "Plot Size",
      value: `${parcel.plotSqYd.toLocaleString("en-IN")} SQ YD`,
      sub: `${sqm(parcel.plotSqYd).toLocaleString("en-IN")} SQ M`,
    },
    { label: "Expressway", value: `${parcel.expresswayKm} KM`, icon: Route },
    { label: "Airport", value: `${parcel.airportKm} KM`, icon: Plane },
  ];

  return (
    <div className="relative min-h-screen bg-background font-sans text-foreground antialiased" data-testid="parcel-page">
      <BlueprintBackground />
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-10">
          <Link
            to="/"
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
            data-testid="parcel-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All parcels
          </Link>
          <p className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground sm:block">
            {parcel.zone} · {parcel.surveyNo ? `SY ${parcel.surveyNo}` : `SEC ${parcel.sector}`} · {extras.plotCode}
          </p>
          <button
            onClick={onToggleTheme}
            className="flex h-9 w-9 items-center justify-center border border-border transition-colors duration-200 hover:bg-secondary"
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 md:px-10 md:py-14">
        <div className="grid gap-10 lg:grid-cols-12">
          <motion.div {...fadeUp(0)} className="lg:col-span-7">
            <div className="border border-border bg-card p-2">
              <div className="relative h-[320px] overflow-hidden md:h-[440px]">
                <img
                  src={gallery[activeImg]}
                  alt={parcel.name}
                  className="h-full w-full object-cover saturate-[.55] contrast-[1.05]"
                  data-testid="parcel-main-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                <p className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">
                  Fig. 0{activeImg + 1} — {parcel.name}
                </p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3" data-testid="parcel-gallery">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`relative h-20 overflow-hidden border transition-colors duration-200 md:h-24 ${
                    activeImg === i ? "border-accent" : "border-border"
                  }`}
                  data-testid={`parcel-thumb-${i}`}
                >
                  <img
                    src={g}
                    alt={`${parcel.name} view ${i + 1}`}
                    className="h-full w-full object-cover saturate-[.55]"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp(0.1)} className="lg:col-span-5">
            <div className="flex flex-wrap gap-2">
              <span className="border border-border bg-card px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em]">
                {parcel.zone} · {parcel.zoneLabel}
              </span>
              <span
                className="flex items-center gap-1.5 border border-border bg-card px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em]"
                data-testid="parcel-rera-badge"
              >
                {registered ? (
                  <ShieldCheck className="h-3 w-3 text-accent" />
                ) : (
                  <Clock3 className="h-3 w-3 text-ember" />
                )}
                RERA {registered ? "Registered" : "Applied"}
              </span>
            </div>
            <h1
              className="mt-5 font-display text-4xl font-medium uppercase tracking-tight md:text-5xl"
              data-testid="parcel-name"
            >
              {parcel.name}
            </h1>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              {parcel.coords} · {parcel.surveyNo ? `Survey ${parcel.surveyNo}` : `Sector ${parcel.sector}`}
            </p>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-muted-foreground md:text-base">
              {extras.overview}
            </p>

            <div className="mt-6 grid grid-cols-2 gap-px border border-border bg-border">
              {metrics.map((m) => (
                <div key={m.label} className="bg-card p-3.5">
                  <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                    {m.icon && <m.icon className="h-3 w-3" />}
                    {m.label}
                  </p>
                  <p className="mt-1 font-mono text-sm font-bold">{m.value}</p>
                  {m.sub && (
                    <p className="font-mono text-[10px] text-muted-foreground">{m.sub}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-end justify-between border-t border-border pt-5">
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                  Base Rate
                </p>
                <p className="font-display text-3xl font-extrabold tracking-tight" data-testid="parcel-rate">
                  {inr(parcel.pricePerSqYd)}
                  <span className="font-mono text-[10px] font-normal text-muted-foreground"> / SQ YD</span>
                </p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                  All-in Base
                </p>
                <p className="font-mono text-base font-bold text-accent" data-testid="parcel-total">
                  {inr(total)}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                onClick={() => setLead({ property: parcel, mode: "interest" })}
                className="h-12 flex-1 rounded-none font-mono text-[11px] uppercase tracking-[0.15em]"
                data-testid="parcel-express-interest"
              >
                Express Interest
                <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                onClick={() => setLead({ property: parcel, mode: "visit" })}
                className="h-12 rounded-none font-mono text-[11px] uppercase tracking-[0.15em]"
                data-testid="parcel-site-visit"
              >
                <Video className="mr-1.5 h-3.5 w-3.5" />
                Site Visit
              </Button>
              <Button
                variant="outline"
                onClick={() => setCalcOpen(true)}
                className="h-12 rounded-none font-mono text-[11px] uppercase tracking-[0.15em]"
                data-testid="parcel-calc-btn"
              >
                <CalculatorIcon className="mr-1.5 h-3.5 w-3.5" />
                Run Numbers
              </Button>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              Ref {parcel.reraId} · Mock data, illustrative
            </p>
          </motion.div>
        </div>

        <div className="mt-16 grid gap-10 lg:grid-cols-12 md:mt-24">
          <motion.section {...fadeUp(0)} className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent">
              Scheme Map
            </p>
            <h2 className="mt-3 font-display text-2xl font-medium uppercase tracking-tight md:text-3xl">
              Position in the TP scheme
            </h2>
            <div className="mt-6 border border-border bg-card p-4">
              <SchemeMap
                index={PROPERTIES.indexOf(parcel)}
                zone={parcel.zone}
                sector={parcel.sector}
                plotCode={extras.plotCode}
              />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Illustrative scheme diagram — final demarcation per DSIRDA records
            </p>
          </motion.section>

          <motion.section {...fadeUp(0.1)} className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent">
              Documents
            </p>
            <h2 className="mt-3 font-display text-2xl font-medium uppercase tracking-tight md:text-3xl">
              Title & compliance
            </h2>
            <div className="mt-6 border-t border-border" data-testid="parcel-documents">
              {docs.map((d, i) => (
                <div
                  key={d.name}
                  className="flex items-center gap-4 border-b border-border py-4"
                  data-testid={`parcel-doc-${i}`}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-border text-muted-foreground">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{d.name}</p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      {d.ref} · {d.meta}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => requestDoc(d.name)}
                    className="h-9 shrink-0 rounded-none px-3 font-mono text-[10px] uppercase tracking-[0.15em]"
                    data-testid={`doc-request-${i}`}
                  >
                    Request
                  </Button>
                </div>
              ))}
            </div>
          </motion.section>
        </div>
      </main>

      <Calculator open={calcOpen} onOpenChange={setCalcOpen} seed={calcSeed} />
      <LeadModal lead={lead} onClose={() => setLead(null)} />
    </div>
  );
}
